import { useState } from "react";
import { MessageCircle, Phone, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useI18n } from "@/lib/i18n";
import { waLink, fmtDate } from "@/lib/api";
import { Card, SectionTitle, ContractorCard, EstimateView, useAct } from "@/components/common";
import { RateVisit } from "@/components/Rating";

const PRE = ["new", "needs_review", "contacting_customer", "ready_to_assign"];

function Chosen({ r }) {
  const { t } = useI18n();
  const c = r.contractor;
  return (
    <Card data-testid="chosen-contractor">
      <SectionTitle>{t("d.chosen")}</SectionTitle>
      <ContractorCard c={c} chosen />
      {r.assignment?.status === "pending" && <p className="mt-3 text-sm text-amber-700 flex items-center gap-2"><Clock className="h-4 w-4" />{t("d.pending_accept")}</p>}
      {c.phone && (
        <div className="mt-4 grid grid-cols-2 gap-2">
          <Button asChild className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="contact-whatsapp-btn">
            <a href={`${waLink(c.phone)}?text=${encodeURIComponent(`NicaFix ${r.number}`)}`} target="_blank" rel="noreferrer"><MessageCircle className="h-4 w-4 me-2" />{t("d.whatsapp")}</a>
          </Button>
          <Button asChild variant="outline" className="rounded-full" data-testid="contact-call-btn"><a href={`tel:${c.phone}`}><Phone className="h-4 w-4 me-2" />{t("d.call")}</a></Button>
        </div>
      )}
    </Card>
  );
}

export default function CustomerPanel({ r, reload }) {
  const { t } = useI18n();
  const [busy, act] = useAct(reload);
  const [note, setNote] = useState("");
  const recs = [...(r.recommended_profiles || []), ...(r.combined_profiles || [])].filter((c, i, a) => a.findIndex((x) => x.id === c.id) === i);
  const canPick = !r.contractor && PRE.includes(r.status);
  const est = r.estimate;
  return (
    <div className="space-y-6">
      <RateVisit r={r} reload={reload} />
      {canPick && (
        <Card data-testid="customer-recommendations">
          <SectionTitle>{t("d.recs_t")}</SectionTitle>
          {recs.length ? (
            <>
              <p className="text-sm text-slate-500 mb-4">{t((r.recommended_profiles || []).length === 1 ? "d.recs_d1" : "d.recs_d", { service: t(r.kind === "cleaning" ? "cl.label" : `svc.${r.service}`), city: r.city })}</p>
              <div className="space-y-3">{recs.map((c) => <ContractorCard key={c.id} c={c} busy={busy} onSelect={(id) => act(`/requests/${r.id}/select`, { contractor_id: id })} />)}</div>
            </>
          ) : <p className="text-sm text-slate-500">{t("d.recs_none")}</p>}
        </Card>
      )}
      {r.contractor && <Chosen r={r} />}
      {est && (
        <Card data-testid="customer-estimate">
          <SectionTitle>{t("d.estimate")}</SectionTitle>
          <EstimateView est={est} />
          {est.status === "sent" && (
            <div className="mt-4 space-y-2">
              <Button className="w-full rounded-full bg-teal-600 hover:bg-teal-700" disabled={busy} onClick={() => act(`/requests/${r.id}/estimate/decision`, { approve: true })} data-testid="estimate-approve-btn">{t("d.approve")}</Button>
              <Textarea rows={2} placeholder={t("d.reason")} value={note} onChange={(e) => setNote(e.target.value)} data-testid="estimate-decline-note" />
              <Button variant="outline" className="w-full rounded-full" disabled={busy} onClick={() => act(`/requests/${r.id}/estimate/decision`, { approve: false, note })} data-testid="estimate-decline-btn">{t("d.decline_est")}</Button>
            </div>
          )}
        </Card>
      )}
      {r.appointments?.length > 0 && (
        <Card><SectionTitle>{t("d.proposed")}</SectionTitle><ul className="text-sm space-y-1" data-testid="customer-appointments">{r.appointments.map((a, i) => <li key={i}>• {a.time}</li>)}</ul></Card>
      )}
      {r.status === "completed" && (
        <Card data-testid="customer-completion">
          <Button className="w-full rounded-full bg-teal-600 hover:bg-teal-700" disabled={busy} onClick={() => act(`/requests/${r.id}/confirm`)} data-testid="confirm-complete-btn">{t("d.confirm")}</Button>
          <Textarea className="mt-3" rows={2} placeholder={t("d.concern")} value={note} onChange={(e) => setNote(e.target.value)} data-testid="concern-input" />
          <Button variant="outline" className="w-full mt-2 rounded-full border-red-300 text-red-700" disabled={busy} onClick={() => act(`/requests/${r.id}/concern`, { note })} data-testid="raise-concern-btn">{t("d.concern")}</Button>
        </Card>
      )}
      {r.customer_updates?.length > 0 && (
        <Card><SectionTitle>{t("d.updates")}</SectionTitle>
          <div className="space-y-3" data-testid="customer-updates">{[...r.customer_updates].reverse().map((u) => (
            <div key={u.id} className="rounded-xl bg-teal-50 p-3"><p className="text-sm font-semibold">{u.title}</p><p className="text-sm text-slate-600">{u.body}</p><p className="text-[10px] font-mono text-slate-400">{fmtDate(u.at)}</p></div>
          ))}</div>
        </Card>
      )}
      {!["in_progress", "completed", "closed", "customer_canceled"].includes(r.status) && (
        <Button variant="ghost" className="w-full text-slate-500" disabled={busy} onClick={() => window.confirm(t("d.cancel") + "?") && act(`/requests/${r.id}/cancel`, { note: "" })} data-testid="cancel-request-btn">{t("d.cancel")}</Button>
      )}
    </div>
  );
}
